import { useSeo } from "../lib/seo";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { CATEGORIES } from "../data/categories";
import { useAuth } from "../context/AuthContext";
import { useData } from "../context/DataContext";
import { PageShell, Panel } from "../components/PageShell";
import { PopButton } from "../components/PopButton";
import { SoundToggle } from "../components/SoundToggle";

function FavouriteCategories() {
  const { prefs, setPrefs } = useData();
  const favs = prefs.favourite_categories || [];
  return (
    <div>
      <p className="font-display text-lg font-bold uppercase">Favourite categories</p>
      <p className="mb-3 text-sm font-medium text-zinc-600">Shown first on home, and picked more often when you hit I'M STUCK.</p>
      <div className="flex flex-wrap gap-2">
        {CATEGORIES.map((c) => (
          <button key={c.id} className="chip" data-active={favs.includes(c.id)} data-testid={`account-fav-category-${c.id}`}
            onClick={() => setPrefs({ favourite_categories: favs.includes(c.id) ? favs.filter((f) => f !== c.id) : [...favs, c.id] })}>
            {c.name}
          </button>
        ))}
      </div>
    </div>
  );
}

function FastModeToggle() {
  const { prefs, setPrefs } = useData();
  return (
    <button onClick={() => setPrefs({ reduced_motion: !prefs.reduced_motion })} data-testid="fast-mode-toggle" aria-pressed={prefs.reduced_motion}
      className="flex w-full items-center justify-between gap-4 text-left">
      <span><span className="block font-display text-lg font-bold uppercase">Fast mode</span><span className="text-sm font-medium text-zinc-600">Skip animations and show results instantly.</span></span>
      <span className={`relative h-8 w-14 shrink-0 rounded-full border-[3px] border-ink transition-colors ${prefs.reduced_motion ? "bg-pop-green" : "bg-zinc-200"}`}>
        <span className={`absolute top-0.5 h-5 w-5 rounded-full border-2 border-ink bg-white transition-transform ${prefs.reduced_motion ? "translate-x-6" : "translate-x-0.5"}`} />
      </span>
    </button>
  );
}

function MyGroups() {
  const [groups, setGroups] = useState<{ id: string; title: string; status: string }[]>([]);
  useEffect(() => { api.get("/me/groups").then((r) => setGroups(r.data)).catch(() => undefined); }, []);
  if (!groups.length) return null;
  return (
    <Panel>
      <p className="mb-3 font-display text-lg font-bold uppercase">Your group votes</p>
      <ul className="space-y-2" data-testid="my-groups-list">
        {groups.map((g) => (
          <li key={g.id}><Link to={`/g/${g.id}`} className="flex justify-between gap-3 rounded-xl border-2 border-ink px-3 py-2 font-bold hover:bg-pop-yellow">
            <span className="truncate">{g.title}</span><span className="text-xs uppercase text-zinc-500">{g.status}</span></Link></li>
        ))}
      </ul>
    </Panel>
  );
}

export default function AccountPage() {
  useSeo({ title: "Your account | DITHER", description: "DITHER account and preferences.", noindex: true });
  const { user, logout } = useAuth();
  const { history, favourites } = useData();
  return (
    <PageShell title="You" subtitle={user ? `Signed in as ${user.email}` : "Playing as a guest"} testId="account-page">
      <div className="space-y-5">
        <Panel>
          {user ? (
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="font-display text-2xl font-bold uppercase" data-testid="account-user-name">{user.name}</p>
                <p className="text-sm font-semibold text-zinc-600">{history.length} decisions · {favourites.length} saved · synced to your account</p>
              </div>
              <PopButton tone="white" size="sm" onClick={async () => { await logout(); toast.success("Signed out"); }} data-testid="logout-button"><LogOut className="h-4 w-4" />Sign out</PopButton>
            </div>
          ) : (
            <div>
              <p className="font-display text-2xl font-bold uppercase">Keep your stuff</p>
              <p className="mb-4 text-sm font-semibold text-zinc-600">Guest history and favourites live in this browser only. Sign in and we'll move them to your account.</p>
              <Link to="/auth" className="btn-pop inline-flex rounded-2xl bg-pop-orange px-5 py-3 font-display font-bold uppercase text-white" data-testid="account-signin-link">Sign in / Sign up</Link>
            </div>
          )}
        </Panel>
        <Panel><FavouriteCategories /></Panel>
        <Panel><FastModeToggle /></Panel>
        <Panel>
          <div className="flex items-center justify-between gap-4">
            <span><span className="block font-display text-lg font-bold uppercase">Sound effects</span><span className="text-sm font-medium text-zinc-600">Countdown ticks, wheel clicks and a winner fanfare.</span></span>
            <SoundToggle place="account" />
          </div>
        </Panel>
        {user && user.role === "admin" && (
          <Link to="/admin/analytics" className="ua-btn block py-3 text-center text-lg" data-testid="account-analytics-link">Traffic & top tools</Link>
        )}
        {user && <MyGroups />}
      </div>
    </PageShell>
  );
}
